/**
 * PURPOSE:
 * Render rugby cumulative statistics inside /tabla-acumulada.
 *
 * RESPONSIBILITIES:
 * - Consume the backend-prepared cumulative rugby stats payload.
 * - Render sub-tabs for Tabla de Tries and Conversiones.
 * - Keep heavy aggregation in backend; only format and render tables/cards.
 * - Handle loading, error and empty states without breaking the public page.
 *
 * COLLABORATORS:
 * - src/services/rugbyStatsService.js
 * - src/sections/TablaAcumuladaSection.jsx
 *
 * ROUTE:
 * src/components/TablaAcumulada/RugbyCumulativeStats.jsx
 */

import React, { useEffect, useMemo, useState } from "react";
import { Tab } from "@headlessui/react";
import { getRugbyCumulativeStats } from "../../services/rugbyStatsService.js";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}


function getRowKey(row) {
  return row?.playerKey || row?.docnumber || row?.idperson || row?.idacreditation;
}

function LoadingState() {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="h-32 animate-pulse rounded-[30px] border border-slate-200 bg-slate-100"
          />
        ))}
      </div>

      <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
        <div className="hidden md:block">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse border-b border-slate-100 bg-slate-50"
            />
          ))}
        </div>

        <div className="divide-y divide-slate-100 md:hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="h-28 animate-pulse bg-slate-50" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="rounded-[30px] border border-red-200 bg-red-50 p-6 text-red-700 shadow-sm">
      <div className="text-sm font-semibold">
        No se pudo cargar la estadística acumulada de rugby
      </div>
      <p className="mt-2 text-sm">{message}</p>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-[30px] border border-dashed border-slate-300 bg-white/90 p-10 text-center shadow-sm">
      <h3 className="text-xl font-semibold text-slate-800">{title}</h3>
      {description && (
        <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
      )}
    </div>
  );
}

function StatPill({ label, value, tone = "emerald" }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
  };

  return (
    <div
      className={classNames(
        "rounded-2xl px-4 py-3 text-left ring-1",
        toneMap[tone] || toneMap.emerald
      )}
    >
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-70">
        {label}
      </div>
      <div className="mt-1 text-2xl font-black leading-none">{value}</div>
    </div>
  );
}

function TeamIdentity({ row }) {
  return (
    <div className="flex items-center gap-2 text-left">
      {row.institutionLogo ? (
        <img
          src={row.institutionLogo}
          alt={row.institutionAbbrev || row.playerName}
          className="h-8 w-8 shrink-0 rounded-xl border border-slate-200 bg-white object-cover shadow-sm"
        />
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-[10px] font-bold text-slate-600 shadow-sm">
          {row.institutionAbbrev?.slice(0, 3) || "RUG"}
        </div>
      )}
      <span className="font-semibold text-slate-700">
        {row.institutionAbbrev || "—"}
      </span>
    </div>
  );
}

function TriesDesktopTable({ rows, columns, totalsByEvent, grandTotal }) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-[30px] border border-slate-200/80 bg-white/95 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur-sm">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="w-20 px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                #
              </th>
              <th className="w-32 px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                Siglas
              </th>
              <th className="min-w-[280px] px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                Nombres y apellidos
              </th>
              {columns.map((column) => (
                <th
                  key={column.idevent}
                  className="min-w-[108px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]"
                >
                  {column.label}
                </th>
              ))}
              <th className="min-w-[110px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                Total
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <tr
                key={getRowKey(row)}
                className={classNames(
                  "transition-colors duration-150 hover:bg-emerald-50/40",
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                )}
              >
                <td className="px-4 py-4 text-left text-sm font-semibold text-slate-500">
                  #{row.rank || index + 1}
                </td>
                <td className="px-4 py-4 text-left text-sm">
                  <TeamIdentity row={row} />
                </td>
                <td className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  {row.playerName}
                </td>
                {columns.map((column) => {
                  const value = Number(row.byEvent?.[column.idevent] || 0);
                  return (
                    <td
                      key={`${getRowKey(row)}-${column.idevent}`}
                      className="px-4 py-4 text-center text-sm font-bold text-slate-700"
                    >
                      {value > 0 ? value : ""}
                    </td>
                  );
                })}
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex min-w-[58px] items-center justify-center rounded-2xl bg-emerald-50 px-3 py-2 text-lg font-black text-emerald-600 ring-1 ring-emerald-100">
                    {row.totalTries || 0}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr className="bg-slate-100">
              <td
                colSpan={3}
                className="px-4 py-4 text-right text-sm font-black uppercase tracking-[0.14em] text-slate-700"
              >
                Totales
              </td>
              {columns.map((column) => (
                <td
                  key={`tries-total-${column.idevent}`}
                  className="px-4 py-4 text-center text-sm font-black text-slate-800"
                >
                  {Number(totalsByEvent?.[column.idevent] || 0)}
                </td>
              ))}
              <td className="px-4 py-4 text-center text-sm font-black text-slate-900">
                {grandTotal || 0}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function TriesMobileList({ rows, columns }) {
  return (
    <div className="space-y-3 md:hidden">
      {rows.map((row) => {
        const visibleColumns = columns.filter(
          (column) => Number(row.byEvent?.[column.idevent] || 0) > 0
        );

        return (
          <div
            key={getRowKey(row)}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-left">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  #{row.rank}
                </div>
                <div className="mt-1 truncate text-sm font-bold text-slate-900">
                  {row.playerName}
                </div>
                <div className="mt-1 truncate text-xs font-medium text-slate-500">
                  {row.institutionAbbrev || "Institución"}
                </div>
              </div>

              <div className="inline-flex min-w-[58px] items-center justify-center rounded-2xl bg-emerald-50 px-3 py-2 text-xl font-black text-emerald-600 ring-1 ring-emerald-100">
                {row.totalTries || 0}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {visibleColumns.length ? (
                visibleColumns.map((column) => (
                  <div
                    key={`${getRowKey(row)}-${column.idevent}`}
                    className="rounded-2xl bg-slate-50 px-3 py-2 text-left"
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      {column.label}
                    </div>
                    <div className="mt-1 text-lg font-black text-slate-900">
                      {row.byEvent[column.idevent]}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 rounded-2xl border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-500">
                  Sin tries cargados por fecha.
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TryStatsView({ stats, columns }) {
  const rows = Array.isArray(stats?.rows) ? stats.rows : [];

  if (!rows.length) {
    return (
      <EmptyState
        title="Aún no hay tries publicados para este circuito"
        description="La tabla se alimenta con goles legacy activos de partidos oficializados."
      />
    );
  }

  return (
    <div className="space-y-4">
      <TriesDesktopTable
        rows={rows}
        columns={columns}
        totalsByEvent={stats?.totalsByEvent || {}}
        grandTotal={stats?.grandTotal || 0}
      />
      <TriesMobileList rows={rows} columns={columns} />
    </div>
  );
}

function formatConversionCell(item) {
  const converted = Number(item?.converted || 0);
  const attempts = Number(item?.attempts || 0);

  if (!attempts) return "";
  return `${converted}/${attempts}`;
}

function ConversionsDesktopTable({ rows, columns, totalsByEvent, totals }) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-[30px] border border-slate-200/80 bg-white/95 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur-sm">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-900 text-white">
            <tr>
              <th className="w-20 px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                #
              </th>
              <th className="w-32 px-4 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                Siglas
              </th>
              <th className="min-w-[280px] px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em]">
                Nombres y apellidos
              </th>
              {columns.map((column) => (
                <th
                  key={column.idevent}
                  className="min-w-[108px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]"
                  title="Convertidas / Intentos"
                >
                  {column.label}
                </th>
              ))}
              <th className="min-w-[96px] px-4 py-4 text-center text-xs font-black uppercase tracking-[0.18em] text-emerald-100 bg-emerald-500/20 ring-1 ring-inset ring-emerald-400/30">
                <span className="inline-flex items-center justify-center rounded-full bg-emerald-400/15 px-3 py-1 text-emerald-100 ring-1 ring-emerald-300/25">
                  Conv.
                </span>
              </th>
              <th className="min-w-[90px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                Fall.
              </th>
              <th className="min-w-[100px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                Intentos
              </th>
              <th className="min-w-[90px] px-4 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em]">
                %
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <tr
                key={getRowKey(row)}
                className={classNames(
                  "transition-colors duration-150 hover:bg-blue-50/40",
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                )}
              >
                <td className="px-4 py-4 text-left text-sm font-semibold text-slate-500">
                  #{row.rank || index + 1}
                </td>
                <td className="px-4 py-4 text-left text-sm">
                  <TeamIdentity row={row} />
                </td>
                <td className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  {row.playerName}
                </td>
                {columns.map((column) => (
                  <td
                    key={`${getRowKey(row)}-${column.idevent}`}
                    className="px-4 py-4 text-center text-sm font-bold text-slate-700"
                  >
                    {formatConversionCell(row.byEvent?.[column.idevent])}
                  </td>
                ))}
                <td className="px-4 py-4 text-center bg-emerald-50/80 ring-1 ring-inset ring-emerald-100/80">
                  <span className="inline-flex min-w-[58px] items-center justify-center rounded-2xl bg-white px-3 py-2 text-base font-black text-emerald-700 shadow-sm ring-1 ring-emerald-200">
                    {row.totalConverted || 0}
                  </span>
                </td>
                <td className="px-4 py-4 text-center text-sm font-black text-rose-600">
                  {row.totalMissed || 0}
                </td>
                <td className="px-4 py-4 text-center text-sm font-semibold text-slate-700">
                  {row.totalAttempts || 0}
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex min-w-[64px] items-center justify-center rounded-2xl bg-blue-50 px-3 py-2 text-sm font-black text-blue-600 ring-1 ring-blue-100">
                    {row.effectivenessPct || 0}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr className="bg-slate-100">
              <td
                colSpan={3}
                className="px-4 py-4 text-right text-sm font-black uppercase tracking-[0.14em] text-slate-700"
              >
                Totales
              </td>
              {columns.map((column) => (
                <td
                  key={`conversion-total-${column.idevent}`}
                  className="px-4 py-4 text-center text-sm font-black text-slate-800"
                >
                  {formatConversionCell(totalsByEvent?.[column.idevent]) || "0/0"}
                </td>
              ))}
              <td className="px-4 py-4 text-center bg-emerald-100/90 ring-1 ring-inset ring-emerald-200">
                <span className="inline-flex min-w-[62px] items-center justify-center rounded-2xl bg-white px-3 py-2 text-base font-black text-emerald-800 shadow-sm ring-1 ring-emerald-300">
                  {totals.grandTotalConverted || 0}
                </span>
              </td>
              <td className="px-4 py-4 text-center text-sm font-black text-rose-700">
                {totals.grandTotalMissed || 0}
              </td>
              <td className="px-4 py-4 text-center text-sm font-black text-slate-800">
                {totals.grandTotalAttempts || 0}
              </td>
              <td className="px-4 py-4 text-center text-sm font-black text-blue-700">
                {totals.grandEffectivenessPct || 0}%
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function ConversionsMobileList({ rows, columns }) {
  return (
    <div className="space-y-3 md:hidden">
      {rows.map((row) => {
        const visibleColumns = columns.filter(
          (column) => Number(row.byEvent?.[column.idevent]?.attempts || 0) > 0
        );

        return (
          <div
            key={getRowKey(row)}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-left">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  #{row.rank}
                </div>
                <div className="mt-1 truncate text-sm font-bold text-slate-900">
                  {row.playerName}
                </div>
                <div className="mt-1 truncate text-xs font-medium text-slate-500">
                  {row.institutionAbbrev || "Institución"}
                </div>
              </div>

              <div className="shrink-0 rounded-2xl bg-blue-50 px-3 py-1.5 text-sm font-black text-blue-600 ring-1 ring-blue-100">
                {row.effectivenessPct || 0}%
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="rounded-2xl bg-emerald-50 px-3 py-2 text-center ring-1 ring-emerald-100">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-600/80">
                  Conv.
                </div>
                <div className="mt-1 text-xl font-black text-emerald-600">
                  {row.totalConverted || 0}
                </div>
              </div>
              <div className="rounded-2xl bg-rose-50 px-3 py-2 text-center ring-1 ring-rose-100">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-rose-600/80">
                  Fall.
                </div>
                <div className="mt-1 text-xl font-black text-rose-600">
                  {row.totalMissed || 0}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-3 py-2 text-center ring-1 ring-slate-200">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Intentos
                </div>
                <div className="mt-1 text-xl font-black text-slate-700">
                  {row.totalAttempts || 0}
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {visibleColumns.length ? (
                visibleColumns.map((column) => (
                  <div
                    key={`${getRowKey(row)}-${column.idevent}`}
                    className="rounded-2xl bg-slate-50 px-3 py-2 text-left"
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      {column.label}
                    </div>
                    <div className="mt-1 text-lg font-black text-slate-900">
                      {formatConversionCell(row.byEvent?.[column.idevent])}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 rounded-2xl border border-dashed border-slate-200 px-3 py-3 text-center text-xs text-slate-500">
                  Sin conversiones cargadas por fecha.
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConversionStatsView({ stats, columns }) {
  const rows = Array.isArray(stats?.rows) ? stats.rows : [];

  if (!rows.length) {
    return (
      <EmptyState
        title="Aún no hay conversiones publicadas para este circuito"
        description="La tabla se alimenta con conversiones de partidos oficializados."
      />
    );
  }

  return (
    <div className="space-y-4">
      <ConversionsDesktopTable
        rows={rows}
        columns={columns}
        totalsByEvent={stats?.totalsByEvent || {}}
        totals={stats || {}}
      />
      <ConversionsMobileList rows={rows} columns={columns} />
    </div>
  );
}

export default function RugbyCumulativeStats({ idevent, seriesId }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadStats() {
      if (!idevent) return;

      try {
        setLoading(true);
        setError("");

        const data = await getRugbyCumulativeStats({
          idevent: Number(idevent),
          seriesId,
        });

        if (cancelled) return;
        setPayload(data);
      } catch (err) {
        if (cancelled) return;

        console.error("Error cargando estadística acumulada de rugby:", err);
        setPayload(null);
        setError(
          err?.response?.data?.msg || "Intente nuevamente en unos momentos."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [idevent, seriesId]);

  const columns = useMemo(() => {
    return Array.isArray(payload?.meta?.columns) ? payload.meta.columns : [];
  }, [payload]);

  const triesTotal = Number(payload?.tries?.grandTotal || 0);
  const conversionAttempts = Number(payload?.conversions?.grandTotalAttempts || 0);
  const conversionEffectiveness = Number(payload?.conversions?.grandEffectivenessPct || 0);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  if (!payload?.ok || !columns.length) {
    return (
      <EmptyState
        title="No hay estadística acumulada configurada"
        description="No se encontró una tabla acumulada asociada a este torneo."
      />
    );
  }

  return (
    <div className="w-full space-y-5">
      <div className="rounded-[30px] border border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-blue-50/40 p-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] md:p-5">
        <div className="flex flex-col items-start gap-4 text-left md:flex-row md:items-start md:justify-between">
          <div className="w-full text-left">
            <div className="text-left text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">
              Estadística acumulada
            </div>
            <h2 className="mt-1 text-left text-2xl font-black text-slate-900 md:text-3xl">
              Tries y Conversiones
            </h2>
            <p className="mt-2 max-w-2xl text-left text-sm leading-6 text-slate-500">
              Revisa el acumulado por fecha del circuito.
            </p>
          </div>

          <div className="grid w-full grid-cols-3 gap-3 md:w-auto md:min-w-[430px]">
            <StatPill label="Fechas" value={columns.length} tone="blue" />
            <StatPill label="Tries" value={triesTotal} tone="emerald" />
            <StatPill
              label="Conv. %"
              value={`${conversionEffectiveness}%`}
              tone={conversionAttempts > 0 ? "slate" : "amber"}
            />
          </div>
        </div>
      </div>

      <Tab.Group>
        <Tab.List className="flex w-full gap-2 rounded-[26px] border border-slate-200/80 bg-white/90 p-2 shadow-sm">
          {[
            { key: "tries", label: "Tabla de Tries" },
            { key: "conversions", label: "Conversiones" },
          ].map((item) => (
            <Tab
              key={item.key}
              className={({ selected }) =>
                classNames(
                  "flex-1 rounded-[20px] px-4 py-3 text-sm font-semibold transition-all duration-150 focus:outline-none",
                  selected
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )
              }
            >
              {item.label}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels className="mt-5">
          <Tab.Panel className="focus:outline-none">
            <TryStatsView stats={payload.tries} columns={columns} />
          </Tab.Panel>
          <Tab.Panel className="focus:outline-none">
            <ConversionStatsView stats={payload.conversions} columns={columns} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
